import dayjs from "dayjs";
import { IMAGES_USERS } from "../routes/server/paths";

/**
 * La función `classNames` toma una serie de clases y devuelve una cadena con todas las clases no
 * vacías unidas.
 * @param classes - El parámetro `clases` es un parámetro de descanso que le permite pasar cualquier
 * cantidad de argumentos. Cada argumento representa un nombre de clase que desea incluir en el
 * resultado final.
 * @returns una cadena que consta de todas las clases no vacías unidas con un separador de espacios.
 */
export function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

/**
 * La función calcula el número total de páginas en función del número total de elementos y el número
 * de elementos por página.
 * @param totalItems - El número total de elementos que deben mostrarse o paginarse.
 * @param itemsPerPage - El número de elementos que se mostrarán en cada página.
 * @returns el número total de páginas necesarias para mostrar todos los elementos, dado el número
 * total de elementos y el número de elementos por página.
 */
export function getTotalPages(totalItems, itemsPerPage) {
  return Math.ceil(totalItems / itemsPerPage);
}

/**
 * La función `dateToLocaleString` toma dos parámetros de fecha y devuelve una cadena formateada que
 * representa el rango de fechas.
 * @param iniDate - El parámetro `iniDate` representa la fecha de inicio de un período. Debe
 * ser una cadena de fecha válida que pueda ser analizada por el constructor `Date`. Ej (2021-01-01)
 * @param endDate - El parámetro `endDate` representa la fecha de finalización de un período. Ej. (2021-01-31)
 * @returns una cadena formateada que representa el intervalo de fechas.
 */
export function dateToLocaleString(iniDate, endDate) {
  const initDateObj = new Date(iniDate);
  const endDateObj = new Date(endDate);

  initDateObj.setUTCHours(0, 0, 0, 0);
  endDateObj.setUTCHours(0, 0, 0, 0);

  const initDay = initDateObj.getUTCDate();
  const initMonth = initDateObj.toLocaleString('default', { month: 'long', timeZone: 'UTC' });
  const initYear = initDateObj.getUTCFullYear();

  const endDay = endDateObj.getUTCDate();
  const endMonth = endDateObj.toLocaleString('default', { month: 'long', timeZone: 'UTC' });
  const endYear = endDateObj.getUTCFullYear();

  let result = `del ${initDay}`;
  
  if (initMonth !== endMonth) {
    result = `${initDay} de ${initMonth}`;
    result += ` al ${endDay} de ${endMonth}`;
  } else if (initYear !== endYear) {
    result += ` de ${initMonth} de ${initYear} al ${endDay} de ${endMonth} de ${endYear}`;
  } else {
    result += ` al ${endDay} de ${endMonth}`;
  }

  return result;
}

/**
 * La función SecurePassword comprueba si una contraseña cumple ciertos criterios de seguridad.
 * @param pass - El parámetro `pass` representa la contraseña cuya seguridad debe verificarse.
 * @returns un valor booleano que indica si la contraseña proporcionada cumple con los criterios
 * especificados para ser considerada segura.
 * @description La contraseña debe tener al menos 8 caracteres, al menos un número, al menos una
 * letra mayúscula, al menos una letra minúscula y al menos un carácter especial.
 */
export function securePassword(pass) {
  const regex = /^(?=.*\d)(?=.*[A-Z])(?=.*[@#$%^&+=!])(?=.*[a-zA-Z]).{8,}$/;
  return regex.test(pass)
}

export const regex = {
  movil: /^\+53[56][0-9]{7}|^[56][0-9]{7}/g,
  nauta: /^[a-zA-Z0-9._-]+@nauta\.cu$/,
}

/**
 * La función extrae todos los números de una cadena y los devuelve como una matriz, o devuelve la
 * cadena original si no se encuentra ningún número.
 * @param str - El parámetro `str` es una cadena que contiene una combinación de números y otros
 * caracteres. La función `extractNumbers` está diseñada para extraer todos los números de la cadena
 * dada y devolverlos como una matriz. Si no hay números en la cadena, la función devolverá la cadena
 * original.
 * @returns La función `extractNumbers` devuelve una matriz de números si se encuentra alguna
 * coincidencia en la cadena de entrada `str`. Si no se encuentran coincidencias, devuelve la cadena de
 * entrada original `str`.
 */
export function extractNumbers(str) {
  const regex = /^\+53[56][0-9]{7}|^[56][0-9]{7}/g;
  const matches = str.match(regex);
  
  if (matches) {
    return matches.map(match => match.replace(/^\+53/, ''));
  } else {
    return str;
  }
}

export const cleanNum = (str) => 
  String(str).replace(/[()_\- ]/g, '');

export const convertPlus53To5 = (option) => { // option = { value: '+5351234567', ... }
  const { value } = option;
  return { ...option, value: value.replace(/^\+53/, '') };
};

export function extractNauta(str) {
  const regex = /^[a-zA-Z0-9._-]+@nauta\.cu$/g;
  const matches = str.match(regex);
  
  if (matches) {
    return matches;
  } else {
    return str;
  }
}

export const coins = [
  {
    name: 'Euro/€',
    value: 'eur',
    symbol: '€',
  },
  {
    name: 'Dolar/$',
    value: 'usd',
    symbol: '$',
  },
];

export const getCurrencySymbol = (currency) => {
  const coin = coins.find(coin => coin.value === currency);
  return coin.symbol;
}

export const round = (value, decimals = 2) => {
  return Number(Math.round(value + 'e' + decimals) + 'e-' + decimals);
}

export const formatToCurrency = (value, decimals = 2) => {
  if (typeof value === 'number') return value.toFixed(decimals);
  return value;
}

/**
 * La función `groupBy` toma una matriz de objetos y un nombre de propiedad, y devuelve un objeto donde
 * las claves son valores únicos de la propiedad especificada y los valores son matrices de objetos que
 * tienen el mismo valor para esa propiedad.
 * @param arr - El parámetro `arr` es una matriz de objetos que desea agrupar.
 * @param prop - El parámetro `prop` es una cadena que representa el nombre de la propiedad por la cual
 * se debe agrupar la matriz `arr`.
 * @returns un objeto donde las claves son los valores únicos de la propiedad especificada (prop) en la
 * matriz (arr), y los valores son matrices que contienen los objetos de la matriz original que tienen
 * el valor de propiedad correspondiente.
 */
export function groupBy(arr, prop) {
  return arr.reduce((acc, obj) => {
    const key = obj[prop];
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(obj);
    return acc;
  }, {});
}

export function getDateWithTime(date){
  const dateObj = new Date(date);
  const day = dateObj.getUTCDate();
  const month = dateObj.toLocaleString('default', { month: 'short', timeZone: 'UTC' });
  const year = dateObj.getUTCFullYear();
  const hours = dateObj.getUTCHours();
  const minutes = dateObj.getUTCMinutes();
  const seconds = dateObj.getUTCSeconds();
  return `${day} ${month}, ${year} / ${hours}:${minutes}:${seconds}`;
}

export function getDiffDates(dateInit, dateEnd, type){
  const date1 = dayjs(dateInit);
  const date2 = dayjs(dateEnd);
  return date2.diff(date1, type)
}

export const validateEmail = (email) => {
  const regex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  return regex.test(email);
};

export const validatePhone = (phone) => {
  const regex = /^[+\d\s]+$/;
  return regex.test(phone);
};

export function sortArray(array, property, order) {
  // add some validations
  if (typeof array !== 'object' 
    || array.length === 0 ) {
    return [];
  }
  if (!Object.keys(array[0] || {})
      .includes(property)) return array

  const newArray = [...array]; // copy array
  
  if (order === 'asc') {
    return newArray.sort((a, b) => {
      if (typeof a[property] === 'string' && typeof b[property] === 'string' && a[property] && b[property]) {
        return a[property].localeCompare(b[property]);
      } else if (typeof a[property] === 'number' && typeof b[property] === 'number' && a[property] && b[property]) {
        return a[property] - b[property];
      } else if (property === 'active' && a[property] && b[property]) {
        return a[property].active - b[property].active;
      }
      return 0;
    });
  } else if (order === 'desc') {
    return newArray.sort((a, b) => {
      if (typeof a[property] === 'string' && typeof b[property] === 'string' && a[property] && b[property]) {
        return b[property].localeCompare(a[property]);
      } else if (typeof a[property] === 'number' && typeof b[property] === 'number' && a[property] && b[property]) {
        return b[property] - a[property];
      } else if (property === 'active' && a[property] && b[property]) {
        return b[property].active - a[property].active;
      }
      return 0;
    });
  }
  return newArray;
}


/* La función `getAvatar` toma una URL como entrada y devuelve la URL del avatar. Si la URL de entrada
ya contiene una ruta (es decir, tiene más de un `/`), se devuelve tal cual. De lo contrario, agrega
la URL de entrada a la ruta `IMAGES_USERS` y devuelve la URL resultante. */
export const getAvatar = (url) => (
  String(url).split('/').length > 1 
    ? url
    : IMAGES_USERS + url
)

/**
 * Checks if a given date is before today.
 * @param {Date} date - The date to be checked.
 * @returns {boolean} - True if the date is before today, false otherwise.
 */
export const dateBeforeToday = (date) => {
  const today = dayjs();
  const currentDate = dayjs(date).endOf('day');
  return currentDate.isBefore(today);
}